import {
  nextTick,
} from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import useAnnotationDatabase from '@/composables/annotationTool/useAnnotationDatabase.js';
import useTaskHistory from '@/composables/annotationTool/useTaskHistory.js';
import { useEditorTaskStore } from '@/stores/useEditorTaskStore.js';
import { useEditorStore } from '@/stores/useEditorStore.js';
import { storeToRefs } from 'pinia';

const SUBMIT_TIMEOUT_MS = 60000;

export default function useTasks() {
  const datastore = new DatastoreConnect();

  function hasAnnotationChanges(annotations) {
    return Boolean(
      annotations
      && (
        annotations.create_annotations?.length
        || annotations.update_annotations?.length
        || annotations.delete_annotations?.length
      ),
    );
  }

  const editorTaskStore = useEditorTaskStore();
  const {
    submitType,
    imageFilters,
    activeImageIndex,
    currentTaskImage,
    pendingTaskImages,
    isTaskComplete,
    reviewSettings,
    remainingImagesCount,
    totalImagesCount,
    auditorType,
    isFetchingNewImage,
    sourceDatasetId,
    sourceDataset,
    sourceAnnotationSet,
    hasSourceAnnotationSet,
    destinationAnnotationSet,
    groups,
    hasReviewTask,
    isAuditMode,
  } = storeToRefs(editorTaskStore);
  const { $reset: resetEditorTaskStore } = editorTaskStore;

  const editorStore = useEditorStore();
  const {
    currentSample,
    annotationSetToEdit,
  } = storeToRefs(editorStore);
  const { $reset: resetEditorStore, clearSample, copyAnnotationsObject } = editorStore;

  const {
    doneImages,
    doneImageOpen,
    getTaskDoneImagesByUser,
    switchToTaskImage,
    switchToNextTaskImage,
    switchToPreviousTaskImage,
  } = useTaskHistory(reviewSettings, currentTaskImage, activeImageIndex);

  const { getAnnotationsForDatabase } = useAnnotationDatabase();

  // Methods
  function handleTaskComplete() {
    isTaskComplete.value = true;
    currentTaskImage.value = null;
    pendingTaskImages.value = [];
    clearSample();
  }

  async function setCurrentTaskImage(newImage) {
    // Set new task image
    if (newImage) {
      currentTaskImage.value = newImage;
      currentSample.value = {
        image: null,
        imageObj: newImage,
        annotations: copyAnnotationsObject(newImage.annotations),
        loaded: false,
      };
    } else {
      currentTaskImage.value = null;
      clearSample();
    }

    if (hasReviewTask.value) {
      getRemainingCount()
        .then(({ total_images, remaining_images }) => {
          remainingImagesCount.value = remaining_images;
          totalImagesCount.value = total_images;
        })
        .catch((error) => {
          console.error(error);
        });
    }

    if (hasReviewTask.value) {
      doneImages.value = await getTaskDoneImagesByUser();
    }
  }

  async function getImageForTask(image_id) {
    let imageResult;
    if (hasReviewTask.value) {
      const params = {
        source_annotation_set_id: reviewSettings.value.reviewTask.source_annotation_set_id,
        review_task_id: reviewSettings.value.reviewTask.id,
        limit: 3,
        images_filter: { dataset_id: reviewSettings.value.reviewTask.dataset_id },
        annotations_filter: {},
        aggregate_annotations_filter: {},
      };
      if (imageFilters.value && imageFilters.value.images_filter) {
        params.images_filter = imageFilters.value.images_filter;
      }
      if (imageFilters.value && imageFilters.value.image_files_filter) {
        params.image_files_filter = imageFilters.value.image_files_filter;
      }
      if (imageFilters.value && imageFilters.value.annotations_filter) {
        params.annotations_filter = imageFilters.value.annotations_filter;
      }

      if (reviewSettings.value?.reviewTask.source_annotation_set_id) {
        params.aggregate_annotations_filter.aggregate_annotation_set_ids = [reviewSettings.value.reviewTask.source_annotation_set_id];
      }

      if (auditorType.value === 'annotation') {
        params.annotations_filter.images_with_annotation_set_ids = [reviewSettings.value.reviewTask.source_annotation_set_id];
      }

      if (image_id) {
        params.image_id = image_id;
      } else if (currentSample.value?.imageObj?.id) {
        params.current_image_id = currentSample.value.imageObj.id;
      }

      if (Object.keys(params.annotations_filter).length === 0) {
        delete params.annotations_filter;
      }

      imageResult = await datastore.getLabellingImages(params)
        .then((resp) => {
          if (resp.error !== undefined) {
            throw Error(resp.error.message);
          }
          return resp.result;
        })
        .catch((error) => {
          console.error(error);
          alert(`Failed to get labelling images\n${error.message}`);
        });

      if (!imageResult) {
        handleTaskComplete();
      }
    } else {
      const params = {
        id: reviewSettings.value?.reviewTask.image_id,
        annotation_set_id: reviewSettings.value?.reviewTask.source_annotation_set_id,
      };
      imageResult = await datastore.getImage(params)
        .then((resp) => {
          if (resp.error !== undefined) {
            throw Error(resp.error.message);
          }
          return resp.result;
        })
        .catch((error) => {
          console.error(error);
          alert(`Failed to get labelling image\n${error.message}`);
        });
      imageResult = [imageResult];
    }

    return imageResult;
  }

  async function getRemainingCount() {
    return datastore.getReviewTaskRemainingCount({
      review_task_id: reviewSettings.value.reviewTask.id,
      dataset_id: reviewSettings.value.reviewTask.dataset_id,
    })
      .then((resp) => {
        if (resp.error !== undefined) {
          throw Error(resp.error.message);
        }
        return resp.result;
      })
      .catch((error) => {
        throw error;
      });
  }

  async function setImageReviewStatusDone({ review_task_id, image_id }) {
    const params = { review_task_id, image_id };
    const resp = await datastore.setImageReviewStatusDone(params)
      .catch((error) => {
        console.log(error);
        alert('Failed to set image review status');
      });
    if (!resp || resp.error || !resp.result) {
      if (resp.error) {
        console.error(resp.error);
        alert(`Failed to set image review status\n${resp.error.message}`);
      }
    }
    return resp;
  }

  async function setupTaskImages() {
    // Get new image for task
    pendingTaskImages.value = await getImageForTask(currentSample.value?.imageObj?.id);
    if (pendingTaskImages.value?.length > 0) {
      setCurrentTaskImage(pendingTaskImages.value[0]);
    } else {
      currentTaskImage.value = null;
    }
  }

  async function skipTaskImage() {
    if (hasReviewTask.value && doneImageOpen.value) {
      switchToNextTaskImage();
      getTaskDoneImagesByUser()
        .then((newDoneImages) => {
          doneImages.value = newDoneImages;
        });
      return;
    }

    await datastore.deleteImageReviewStatus({
      review_task_id: reviewSettings.value.reviewTask.id,
      image_id: currentTaskImage.value.id,
    })
      .then((resp) => {
        if (resp.error !== undefined) {
          throw Error(resp.error.message);
        }
        return resp.result;
      })
      .catch((error) => {
        console.error(error.message);
        throw error;
      });
    if (pendingTaskImages.value?.length > 1) {
      setCurrentTaskImage(pendingTaskImages.value[1]);
    }
    pendingTaskImages.value = await getImageForTask(pendingTaskImages.value[1].id);
  }

  async function skipTaskSequence() {
    if (hasReviewTask.value && doneImageOpen.value) {
      switchToNextTaskImage();
      getTaskDoneImagesByUser()
        .then((newDoneImages) => {
          doneImages.value = newDoneImages;
        });
      return;
    }

    await datastore.deleteImageReviewStatus({
      review_task_id: reviewSettings.value.reviewTask.id,
      sequence_id: currentTaskImage.value.sequence_id,
    })
      .then((resp) => {
        if (resp.error !== undefined) {
          throw Error(resp.error.message);
        }
        return resp.result;
      })
      .catch((error) => {
        console.error(error.message);
        throw error;
      });
    if (pendingTaskImages.value?.length > 1) {
      setCurrentTaskImage(pendingTaskImages.value[1]);
    }
    pendingTaskImages.value = await getImageForTask(pendingTaskImages.value[1]);
  }

  async function completeTaskSequence() {
    if (hasReviewTask.value && doneImageOpen.value) {
      switchToNextTaskImage();
      getTaskDoneImagesByUser()
        .then((newDoneImages) => {
          doneImages.value = newDoneImages;
        });
      return;
    }

    await datastore.setImageReviewStatusDone({
      review_task_id: reviewSettings.value.reviewTask.id,
      sequence_id: currentTaskImage.value.sequence_id,
    })
      .then((resp) => {
        if (resp.error !== undefined) {
          throw Error(resp.error.message);
        }
        return resp.result;
      })
      .catch((error) => {
        console.error(error.message);
        throw error;
      });
    if (pendingTaskImages.value?.length > 1) {
      setCurrentTaskImage(pendingTaskImages.value[1]);
    }
    pendingTaskImages.value = await getImageForTask(pendingTaskImages.value[1]);
  }

  async function updateTaskImages() {
    const currentImage = pendingTaskImages.value[0];
    const nextImage = pendingTaskImages.value[1];
    // Get new image for task
    if (pendingTaskImages.value?.length > 1) {
      setCurrentTaskImage(nextImage);
    } else {
      currentTaskImage.value = null;
    }

    pendingTaskImages.value = await getImageForTask(currentImage.id);
    if (currentTaskImage.value === null && pendingTaskImages.value?.length > 0) {
      // New pending images were found
      setCurrentTaskImage(currentImage);
    }
  }

  async function switchToImageID(image_id) {
    // Get new images from the given image_id
    pendingTaskImages.value = await getImageForTask(image_id);
    if (pendingTaskImages.value?.length > 0) {
      setCurrentTaskImage(pendingTaskImages.value[0]);
    } else {
      currentTaskImage.value = null;
    }
  }

  function switchToDoneImage(imageObj) {
    activeImageIndex.value = null;
    currentSample.value = {
      image: null,
      imageObj,
      annotations: copyAnnotationsObject(imageObj.annotations),
      loaded: false,
    };
    const reviewedAnnotations = currentSample.value.imageObj.annotations.filter((anno) => anno.annotation_set_id === reviewSettings.value.reviewTask.dest_annotation_set_id);
    nextTick(() => { currentSample.value.annotations = reviewedAnnotations; });
  }

  async function updateTaskImageFilters(newFilters) {
    this.imageFilters = newFilters;
    // Get new images for task
    pendingTaskImages.value = await getImageForTask(currentTaskImage.value?.id);
    if (pendingTaskImages.value?.length > 0) {
      setCurrentTaskImage(pendingTaskImages.value[0]);
    } else {
      currentTaskImage.value = null;
    }
  }

  async function deleteTaskImageFromDataset(image_id) {
    const param = {
      image_ids: [image_id],
      dataset_id: sourceDataset.value.id,
    };
    const deletedResponse = await datastore.deleteImageFromDataset(param)
      .then((resp) => {
        if (resp.error !== undefined) {
          throw Error(resp.error.message);
        }
        return resp.result;
      })
      .catch(async (error) => {
        console.error(error.message);
        await updateTaskImages();
        throw error;
      });

    if (!doneImageOpen.value) {
      await updateTaskImages();
    } else {
      switchToNextTaskImage();
      await getTaskDoneImagesByUser()
        .then((newDoneImages) => {
          doneImages.value = newDoneImages;
        });
    }

    return deletedResponse;
  }

  async function handleAuditSubmitAllAnnotations() {
    // Submit image audit to the database
    const source_annotation_set_id = reviewSettings.value.reviewTask.source_annotation_set_id;
    const dest_annotation_set_id = reviewSettings.value.reviewTask.dest_annotation_set_id;
    const initialAnnotations = (currentSample.value.imageObj?.annotations ?? []).filter((anno) => anno.annotation_set_id === annotationSetToEdit.value.id);
    const filteredInternalAnnotations = currentSample.value.annotations.filter((anno) => (anno.annotation_set_id === source_annotation_set_id || anno.annotation_set_id === dest_annotation_set_id));
    const annotations = getAnnotationsForDatabase(initialAnnotations, filteredInternalAnnotations, source_annotation_set_id, dest_annotation_set_id, doneImageOpen.value);
    const response = await datastore.submitAuditManualAnnotationTask(
      {
        create_annotations: annotations.create_annotations,
        update_annotations: annotations.update_annotations,
        delete_annotations: annotations.delete_annotations,
        review_task_id: reviewSettings.value.reviewTask?.id,
        review_session_id: reviewSettings.value.reviewSession?.id,
        image_id: currentSample.value.imageObj.id,
        annotation_set_id: reviewSettings.value.reviewTask.dest_annotation_set_id,
        group_id: currentSample.value.imageObj.group_id,
      },
    );

    if (response.error) {
      alert("Failed to submit");
      throw Error("Failed to submit");
    }

    if (!hasReviewTask.value) {
      return;
    }

    // Next done image
    if (doneImageOpen.value) {
      switchToNextTaskImage();
      await getTaskDoneImagesByUser()
        .then((newDoneImages) => {
          doneImages.value = newDoneImages;
        });
      return;
    }

    // Next Pending Task Image
    await updateTaskImages();
  }

  async function handleAuditSubmitAcceptedAnnotations() {
    // Submit image audit to the database
    const source_annotation_set_id = reviewSettings.value.reviewTask.source_annotation_set_id;
    const dest_annotation_set_id = reviewSettings.value.reviewTask.dest_annotation_set_id;
    const initialAnnotations = (currentSample.value.imageObj?.annotations ?? []).filter((anno) => anno.annotation_set_id === annotationSetToEdit.value.id);
    const filteredInternalAnnotations = currentSample.value.annotations.filter((anno) => (anno.annotation_set_id === source_annotation_set_id || anno.annotation_set_id === dest_annotation_set_id));
    const verifiedAnnotations = filteredInternalAnnotations.filter((anno) => anno.reviewStatus === 'verified');
    const annotations = getAnnotationsForDatabase(initialAnnotations, verifiedAnnotations, source_annotation_set_id, dest_annotation_set_id, doneImageOpen.value);
    const response = await datastore.submitAuditManualAnnotationTask(
      {
        create_annotations: annotations.create_annotations,
        update_annotations: annotations.update_annotations,
        delete_annotations: annotations.delete_annotations,
        review_task_id: reviewSettings.value.reviewTask?.id,
        review_session_id: reviewSettings.value.reviewSession?.id,
        image_id: currentSample.value.imageObj.id,
        annotation_set_id: reviewSettings.value.reviewTask.dest_annotation_set_id,
        group_id: currentSample.value.imageObj.group_id,
      },
    );

    if (response.error) {
      alert("Failed to submit");
      throw Error("Failed to submit");
    }

    if (!hasReviewTask.value) {
      return;
    }

    // Next done image
    if (doneImageOpen.value) {
      switchToNextTaskImage();
      await getTaskDoneImagesByUser()
        .then((newDoneImages) => {
          doneImages.value = newDoneImages;
        });
      return;
    }

    // Next Pending Task Image
    await updateTaskImages();
  }

  async function handleFrameAuditSubmitAllAnnotations(imageObj) {
    // Submit image audit to the database
    const initialAnnotations = (imageObj?.annotations ?? []).filter((anno) => anno.annotation_set_id === annotationSetToEdit.value.id);
    const filteredInternalAnnotations = currentSample.value.annotations.filter((anno) => anno.annotation_set_id === annotationSetToEdit.value.id);
    const source_annotation_set_id = reviewSettings.value.reviewTask.source_annotation_set_id;
    const dest_annotation_set_id = reviewSettings.value.reviewTask.dest_annotation_set_id;
    const annotations = getAnnotationsForDatabase(initialAnnotations, filteredInternalAnnotations, source_annotation_set_id, dest_annotation_set_id, doneImageOpen.value);
    const response = await datastore.submitAuditManualAnnotationTask(
      {
        create_annotations: annotations.create_annotations,
        update_annotations: annotations.update_annotations,
        delete_annotations: annotations.delete_annotations,
        review_task_id: reviewSettings.value.reviewTask?.id,
        review_session_id: reviewSettings.value.reviewSession?.id,
        image_id: imageObj.id,
        annotation_set_id: reviewSettings.value.reviewTask.dest_annotation_set_id,
        group_id: imageObj.group_id,
        sequence_id: imageObj.sequence_id,
      },
    );

    if (response.error) {
      alert("Failed to submit");
      throw Error("Failed to submit");
    }

    // Next Pending Task Image
    // await updateTaskImages();
  }

  async function handleFrameAuditSubmitAcceptedAnnotations(imageObj) {
    // Submit image audit to the database
    const initialAnnotations = (imageObj?.annotations ?? []).filter((anno) => anno.annotation_set_id === annotationSetToEdit.value.id);
    const filteredInternalAnnotations = currentSample.value.annotations.filter((anno) => anno.annotation_set_id === annotationSetToEdit.value.id);
    const verifiedAnnotations = filteredInternalAnnotations.filter((anno) => anno.reviewStatus === 'verified');
    const source_annotation_set_id = reviewSettings.value.reviewTask.source_annotation_set_id;
    const dest_annotation_set_id = reviewSettings.value.reviewTask.dest_annotation_set_id;
    const annotations = getAnnotationsForDatabase(initialAnnotations, verifiedAnnotations, source_annotation_set_id, dest_annotation_set_id, doneImageOpen.value);
    const response = await datastore.submitAuditManualAnnotationTask(
      {
        create_annotations: annotations.create_annotations,
        update_annotations: annotations.update_annotations,
        delete_annotations: annotations.delete_annotations,
        review_task_id: reviewSettings.value.reviewTask?.id,
        review_session_id: reviewSettings.value.reviewSession?.id,
        image_id: imageObj.id,
        annotation_set_id: reviewSettings.value.reviewTask.dest_annotation_set_id,
        group_id: imageObj.group_id,
        sequence_id: imageObj.sequence_id,
      },
    );

    if (response.error) {
      alert("Failed to submit");
      throw Error("Failed to submit");
    }

    // Next Pending Task Image
    // await updateTaskImages();
  }

  async function handleLabellingSubmitAnnotations() {
    // Submit image labelling to the database
    const initialAnnotations = (currentSample.value.imageObj?.annotations ?? []).filter((anno) => anno.annotation_set_id === annotationSetToEdit.value.id);
    const filteredInternalAnnotations = currentSample.value.annotations.filter((anno) => anno.annotation_set_id === annotationSetToEdit.value.id);
    const source_annotation_set_id = reviewSettings.value.reviewTask.source_annotation_set_id;
    const dest_annotation_set_id = reviewSettings.value.reviewTask.dest_annotation_set_id;
    const annotations = getAnnotationsForDatabase(initialAnnotations, filteredInternalAnnotations, source_annotation_set_id, dest_annotation_set_id, doneImageOpen.value);

    if (!hasAnnotationChanges(annotations)) {
      if (reviewSettings.value.reviewTask.id) {
        await updateTaskImages();
      }
      return { result: true, no_changes: true };
    }

    const response = await datastore.submitAuditManualAnnotationTask(
      {
        create_annotations: annotations.create_annotations,
        update_annotations: annotations.update_annotations,
        delete_annotations: annotations.delete_annotations,
        review_task_id: reviewSettings.value.reviewTask.id,
        review_session_id: reviewSettings.value.reviewSession?.id,
        image_id: currentSample.value.imageObj.id,
        annotation_set_id: reviewSettings.value.reviewTask.dest_annotation_set_id,
        group_id: currentSample.value.imageObj.group_id,
      },
    );

    if (response.error) {
      alert("Failed to submit");
      throw Error("Failed to submit");
    }

    // Next done image
    if (doneImageOpen.value) {
      switchToNextTaskImage();
      await getTaskDoneImagesByUser()
        .then((newDoneImages) => {
          doneImages.value = newDoneImages;
        });
      return;
    }

    if (reviewSettings.value.reviewTask.id) {
      // Next Pending Task Image
      await updateTaskImages();
    }
  }

   async function handleSaveAllImagesChangedAnnotations(changedAnnotations) {
    // Submit image frame labelling to the database
    let create_annotations=[];
    let update_annotations=[];
    let delete_annotations=[];
    let annotation_set_id=0;


    for (const key in changedAnnotations) {
      let ann=changedAnnotations[key];
      if (ann.status === 'new') {annotation_set_id=ann.annotation_set_id;create_annotations.push(ann);}
      if (ann.status === 'update') {annotation_set_id=ann.annotation_set_id;create_annotations.push(ann);}
      if (ann.status === 'del') {annotation_set_id=ann.annotation_set_id;delete_annotations.push(ann);}
    }

    //Call a function to save annotation for multiple images


    const response = await datastore.submitAuditManualAnnotationTask(
      {
        create_annotations: create_annotations,
        update_annotations: update_annotations,
        delete_annotations: delete_annotations,
        annotation_set_id:annotation_set_id,
      },
    );

    if (response.error) {
      alert("Failed to submit");
      throw Error("Failed to submit");
    }

    // Next Pending Task Image
    // await updateTaskImages();
  }


  async function handleFrameLabellingSubmitAnnotations(imageObj) {
    // Submit image frame labelling to the database
    const initialAnnotations = (imageObj?.annotations ?? []).filter((anno) => anno.annotation_set_id === annotationSetToEdit.value.id);
    const filteredInternalAnnotations = currentSample.value.annotations.filter((anno) => anno.annotation_set_id === annotationSetToEdit.value.id);
    const source_annotation_set_id = reviewSettings.value.reviewTask.source_annotation_set_id;
    const dest_annotation_set_id = reviewSettings.value.reviewTask.dest_annotation_set_id;
    const annotations = getAnnotationsForDatabase(initialAnnotations, filteredInternalAnnotations, source_annotation_set_id, dest_annotation_set_id, doneImageOpen.value);

    if (!hasAnnotationChanges(annotations)) {
      return { result: true, no_changes: true };
    }

    const response = await datastore.submitAuditManualAnnotationTask(
      {
        create_annotations: annotations.create_annotations,
        update_annotations: annotations.update_annotations,
        delete_annotations: annotations.delete_annotations,
        review_task_id: reviewSettings.value.reviewTask.id,
        review_session_id: reviewSettings.value.reviewSession?.id,
        image_id: imageObj.id,
        annotation_set_id: reviewSettings.value.reviewTask.dest_annotation_set_id,
        group_id: imageObj.group_id,
        sequence_id: imageObj.sequence_id,
      },
      AbortSignal.timeout(SUBMIT_TIMEOUT_MS),
    );

    if (response.error) {
      throw Error(response.error.message || 'Failed to submit');
    }

    // Next Pending Task Image
    // await updateTaskImages();
  }

  // async function submitPCDFile() {
  //   const annotation_id = pcdAnnotations.value[0].id;
  //   const newFile = createPCDFile();
  //   const dataConnect = new DatastoreConnect();
  //   await dataConnect.updateAnnotationFile(annotation_id, new Blob([newFile], { type: 'text/plain' }));
  // }

  function acceptAllAnnotations() {
    currentSample.value.annotations.forEach((anno) => {
      anno.reviewStatus = 'verified';
    });
  }

  async function getTaskSequences() {
    const dataConnect = new DatastoreConnect();
    return dataConnect.getFilteredImages({
      combine_sequence_frames: true,
      images_filter: {
        dataset_id: sourceDataset.value.id,
      },
      image_files_filter: {},
      annotations_filter: {},
      only_sequences: true,
      // dataset_id: sourceDataset.value.id,
    })
      .then((resp) => {
        if (resp.error !== undefined) {
          throw Error(resp.error);
        }
        return resp.result;
      })
      .catch((error) => {
        throw error;
      });
  }

  return {
    reviewSettings,
    submitType,
    currentTaskImage,
    pendingTaskImages,
    isTaskComplete,
    sourceDatasetId,
    sourceDataset,
    sourceAnnotationSet,
    hasSourceAnnotationSet,
    destinationAnnotationSet,
    isAuditMode,
    groups,
    remainingImagesCount,
    totalImagesCount,
    auditorType,
    isFetchingNewImage,
    imageFilters,
    hasReviewTask,
    setImageReviewStatusDone,
    setupTaskImages,
    skipTaskImage,
    skipTaskSequence,
    completeTaskSequence,
    updateTaskImages,
    updateTaskImageFilters,
    deleteTaskImageFromDataset,
    handleAuditSubmitAllAnnotations,
    handleAuditSubmitAcceptedAnnotations,
    handleFrameAuditSubmitAllAnnotations,
    handleFrameAuditSubmitAcceptedAnnotations,
    handleLabellingSubmitAnnotations,
    handleFrameLabellingSubmitAnnotations,
    handleSaveAllImagesChangedAnnotations,
    acceptAllAnnotations,
    switchToImageID,
    switchToDoneImage,
    getTaskSequences,
    // Task history
    activeImageIndex,
    doneImages,
    doneImageOpen,
    getTaskDoneImagesByUser,
    switchToTaskImage,
    switchToNextTaskImage,
    switchToPreviousTaskImage,
  };
}
